import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { HardHat, ArrowRight } from "lucide-react";
import { Seo } from "../../components/public/Seo";
import { CtaBand } from "../../components/public/Shared";
import { api, mediaUrl } from "../../lib/api";

function Badge({ status }) {
  if (status === "in_progress")
    return <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#0c1a12] bg-[#62B848] px-2.5 py-1 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-[#0c1a12] animate-pulse" /> In Progress</span>;
  return <span className="text-[11px] font-bold text-white bg-[#224733] px-2.5 py-1 rounded-full">Completed</span>;
}

export default function Gallery() {
  const [projects, setProjects] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([])); }, []);

  const types = useMemo(() => Array.from(new Set((projects || []).map((p) => p.project_type).filter(Boolean))), [projects]);
  const filters = ["all", "in_progress", "completed", ...types];
  const label = (f) => f === "all" ? "All" : f === "in_progress" ? "In Progress" : f === "completed" ? "Completed" : f;

  const items = (projects || []).filter((p) => {
    if (filter === "all") return true;
    if (filter === "in_progress" || filter === "completed") return p.status === filter;
    return p.project_type === filter;
  });

  return (
    <>
      <Seo title="Our Work — Gallery" description="See real West Face Roofing projects across Perth — re-roofs, repairs, gutters and commercial remedial roofing. Before, after and completed jobs." path="/our-work" />
      <section className="pt-32 md:pt-44 pb-12 bg-[#224733] text-white relative overflow-hidden wf-grain">
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
          <p className="text-[#62B848] font-bold tracking-wide uppercase text-sm mb-4">Our work</p>
          <h1 className="font-display font-black tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-none max-w-3xl text-balance">Proof on every rooftop.</h1>
          <p className="mt-6 text-white/70 text-lg max-w-xl leading-relaxed">A look at jobs across Perth — from large-scale commercial re-roofs to crisp residential replacements. Ongoing jobs included, because we're always on the tools.</p>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-[#F8F9FA] min-h-[40vh]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          {projects === null ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="h-[320px] rounded-3xl bg-black/5 animate-pulse" />)}
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-16 md:py-24 max-w-lg mx-auto">
              <div className="grid place-items-center w-20 h-20 rounded-3xl bg-[#224733]/8 text-[#224733] mx-auto mb-6"><HardHat size={38} /></div>
              <h2 className="font-display font-extrabold text-3xl text-[#1A2421] mb-3">Projects coming soon</h2>
              <p className="text-[#4A5550] leading-relaxed mb-8">We're busy building and re-roofing across Perth right now. Our latest completed jobs — with before &amp; after shots — will land here shortly. In the meantime, get a free quote and become our next project.</p>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#62B848] hover:bg-[#4E9B38] text-white font-bold px-7 py-3.5 rounded-full transition-colors">Get a free quote <ArrowRight size={18} /></Link>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-10">
                {filters.map((f) => (
                  <button key={f} data-testid={`gallery-filter-${f}`} onClick={() => setFilter(f)}
                    className={`px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${filter === f ? "bg-[#224733] text-white" : "bg-white text-[#224733] border border-black/10 hover:border-[#62B848]"}`}>{label(f)}</button>
                ))}
              </div>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((p, i) => {
                  const cover = p.photos?.[0] || p.after_url || p.before_url;
                  return (
                    <motion.div key={p.id} layout initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true, margin: "-40px" }} transition={{ delay: (i % 3) * 0.06 }}>
                      <Link to={`/our-work/${p.slug}`} data-testid={`project-link-${p.slug}`} className="group relative block overflow-hidden rounded-3xl h-[320px]">
                        {cover ? <img src={mediaUrl(cover)} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                          : <div className="w-full h-full bg-[#224733]/10 grid place-items-center text-[#224733]/40"><HardHat size={40} /></div>}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent" />
                        <div className="absolute top-4 left-4"><Badge status={p.status} /></div>
                        <div className="absolute bottom-0 left-0 p-5">
                          <div className="text-white font-display font-bold text-lg leading-tight">{p.title}</div>
                          <div className="text-white/70 text-sm mt-1">{[p.suburb, p.project_type].filter(Boolean).join(" · ")}</div>
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>
      <CtaBand />
    </>
  );
}
